/**
 * AddBulk Hooks
 * Custom hooks for bulk upload state management and logic
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { SelectOption } from "components/dropdown/Dropdown";
import {
  fetchProjects,
  fetchSites,
  fetchContactTypes,
  fetchProgrammes,
  fetchSurveyTemplates,
  uploadCSV,
} from "./AddBulk.api";
import { PAGES, UploadData, UploadResponse, UploadPayload } from "./AddBulk.types";
import { DEFAULT_INITIAL_VALUES, SURVEY_ACTIONS } from "./AddBulk.config";
import { buildPayload, downloadTemplateFile } from "./AddBulk.utils";

/**
 * Hook to manage entity-specific data fetching (sites, projects, contact types, etc.)
 * @param noun - Entity type
 * @param parentSelectedId - Parent ID for pre-selection (optional)
 * @returns Entity data state and loading state
 */
export const useEntityData = (noun: PAGES, parentSelectedId?: number) => {
  const [siteData, setSiteData] = useState<SelectOption[]>([]);
  const [projectData, setProjectData] = useState<SelectOption[]>([]);
  const [contactAccessTypes, setContactAccessTypes] = useState<SelectOption[]>([]);
  const [contactTypes, setContactTypes] = useState<SelectOption[]>([]);
  const [programmes, setProgrammes] = useState<SelectOption[]>([]);
  const [surveyTemplates, setSurveyTemplates] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState<boolean>(noun === PAGES.CONTACT || noun === PAGES.SURVEY);

  useEffect(() => {
    const fetchData = async () => {
      if (noun === PAGES.CONTACT) {
        const [projects, sites, contactTypesData] = await Promise.all([
          fetchProjects(),
          fetchSites(),
          fetchContactTypes(),
        ]);
        setProjectData(projects);
        setSiteData(sites);
        setContactTypes(contactTypesData.contactTypes);
        setContactAccessTypes(contactTypesData.contactAccessTypes);
        setLoading(false);
      } else if (noun === PAGES.SURVEY) {
        const [programmesData, templatesData] = await Promise.all([
          fetchProgrammes(),
          fetchSurveyTemplates(),
        ]);
        setProgrammes(programmesData);
        setSurveyTemplates(templatesData);
        setLoading(false);
      }
    };

    fetchData();
  }, [noun]);

  // Compute initial form data based on entity type and fetched data
  const initialData = useMemo<UploadData>(() => {
    if (noun === PAGES.CONTACT && contactAccessTypes.length > 0) {
      const defaultContactTypeId = parentSelectedId
        ? contactAccessTypes.find((type) => type.label.trim().toLowerCase() === "project")?.value
        : contactAccessTypes.find((type) => type.label.trim().toLowerCase() === "global")?.value;

      return {
        contactTypesId: defaultContactTypeId,
        contactTypesIds: [...contactAccessTypes],
        siteData: [...siteData],
        projectData: [...projectData],
        projectsID: parentSelectedId || null,
        sitesID: null,
        csvFile: null,
      };
    } else if (noun === PAGES.SURVEY && programmes.length > 0 && surveyTemplates.length > 0) {
      return {
        action: SURVEY_ACTIONS[0].value,
        actions: [...SURVEY_ACTIONS],
        programmesId: undefined,
        programmes: [...programmes],
        surveyTemplatesId: undefined,
        surveyTemplates: [...surveyTemplates],
        csvFile: null,
      };
    }

    return DEFAULT_INITIAL_VALUES;
  }, [
    noun,
    siteData,
    projectData,
    contactAccessTypes,
    programmes,
    surveyTemplates,
    parentSelectedId,
  ]);

  return {
    siteData,
    projectData,
    contactAccessTypes,
    contactTypes,
    programmes,
    surveyTemplates,
    initialData,
    loading,
  };
};

/**
 * Hook to manage CSV file upload state and logic
 * @returns Upload state and handlers
 */
export const useCSVUpload = () => {
  const [csvFile, setCSVFile] = useState<any>(undefined);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [uploadErrorMessages, setUploadErrorMessages] = useState<string[] | null>(null);
  const [success, setSuccess] = useState<UploadResponse | undefined>();

  const resetUploadState = useCallback(() => {
    setUploadError(false);
    setUploadErrorMessages(null);
    setSuccess(undefined);
  }, []);

  const handleFileChange = useCallback((file: any) => {
    setCSVFile(file);
  }, []);

  const resetFile = useCallback(() => {
    setCSVFile(undefined);
  }, []);

  const performUpload = useCallback(
    async (payload: UploadPayload, endpoint: string) => {
      resetUploadState();
      const result = await uploadCSV(payload, endpoint);

      if (result.success && result.data) {
        setSuccess(result.data);
      } else {
        setUploadError(true);
        setUploadErrorMessages(result.errors || null);
      }
    },
    [resetUploadState]
  );

  return {
    csvFile,
    uploadError,
    uploadErrorMessages,
    success,
    handleFileChange,
    resetFile,
    performUpload,
    resetUploadState,
  };
};

/**
 * Hook to manage template file download
 * @param url - Template file URL
 * @param noun - Entity type
 * @returns Download handler and error state
 */
export const useTemplateDownload = (url: string, noun: PAGES) => {
  const [downloadError, setDownloadError] = useState<boolean>(false);

  const handleDownload = useCallback(async () => {
    try {
      await downloadTemplateFile(url, noun);
      setDownloadError(false);
    } catch (error) {
      console.error("Template download failed:", error);
      setDownloadError(true);
    }
  }, [url, noun]);

  return {
    downloadError,
    handleDownload,
  };
};

/**
 * Main hook to orchestrate AddBulk form logic
 * @param noun - Entity type
 * @param url - Upload endpoint URL
 * @param parentSelectedIdName - Parent ID field name (optional)
 * @param parentSelectedId - Parent ID value (optional)
 * @param extraPayloadItems - Additional payload items (optional)
 * @param onDone - Callback when upload completes successfully
 * @returns All form state and handlers
 */
export const useAddBulkForm = (
  noun: PAGES,
  url: string,
  parentSelectedIdName?: string,
  parentSelectedId?: number,
  extraPayloadItems?: { [fieldName: string]: any },
  onDone?: () => void
) => {
  const entityData = useEntityData(noun, parentSelectedId);
  const uploadState = useCSVUpload();
  const templateDownload = useTemplateDownload(url, noun);

  // Trigger onDone callback when upload succeeds
  useEffect(() => {
    if (uploadState.success?.countUploaded && onDone) {
      onDone();
    }
  }, [uploadState.success, onDone]);

  const handleSubmit = useCallback(
    async (values: UploadData) => {
      const payload = buildPayload(
        values,
        noun,
        parentSelectedIdName,
        parentSelectedId,
        extraPayloadItems,
        entityData.contactAccessTypes
      );

      if (Object.keys(payload).length > 0) {
        await uploadState.performUpload(payload, url);
      }
    },
    [
      noun,
      url,
      parentSelectedIdName,
      parentSelectedId,
      extraPayloadItems,
      entityData.contactAccessTypes,
      uploadState,
    ]
  );

  return {
    ...entityData,
    ...uploadState,
    ...templateDownload,
    handleSubmit,
  };
};
